import * as Yup from "yup";

import { ApiError, getEntryByCompetitorNumber } from "../../../../api";
import { ApiCompetition, ApiInlineUser, ApiProfile } from "../../../../types";

const getSchema = (categoryUUIDs: string[], hasCompetitorNumbers: boolean) => {
  const base = Yup.object().shape({
    firstName: Yup.string().required("Required"),
    lastName: Yup.string().required("Required"),
    profilePicture: Yup.mixed<File | { uuid: string; url: string }>()
      .optional()
      .test(
        "FILE_FORMAT",
        "Please select an image",
        (value: File | { uuid: string; url: string } | undefined) => {
          return value instanceof File
            ? value?.type?.startsWith("image/")
            : true;
        }
      ),
    category: Yup.string().required("Required").oneOf(categoryUUIDs),
  });
  if (hasCompetitorNumbers) {
    return base.shape({
      competitorNumber: Yup.string().required("required"),
    });
  }
  return base;
};

export type ProfileValues = {
  firstName: string;
  lastName: string;
  category: string;
  profilePicture?: File | { uuid: string; url: string };
  competitorNumber: string;
};

type Params = {
  profile?: ApiProfile | ApiInlineUser;
  competition?: ApiCompetition;
  previousCategoryUUID?: string;
  withCompetitorNumber?: boolean;
};

export const getPrefillDataForCompetitorNumber = async (
  competitionUUID: string,
  competitorNumber: string
): Promise<Omit<
  ProfileValues,
  "profilePicture" | "competitorNumber"
> | null> => {
  try {
    const entry = await getEntryByCompetitorNumber(
      competitionUUID,
      competitorNumber
    );
    return {
      firstName: entry.user.firstName,
      lastName: entry.user.lastName,
      category: entry.category.uuid,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
};

export default function useProfileForm({
  profile,
  competition,
  previousCategoryUUID,
  withCompetitorNumber = true,
}: Params) {
  const schema = getSchema(
    competition?.stages?.[0]?.categories.map((x) => x.uuid) || [],
    (withCompetitorNumber && competition?.hasCompetitorNumbers) || false
  );

  const initialValues: ProfileValues = {
    firstName: profile?.firstName || "",
    lastName: profile?.lastName || "",
    category: previousCategoryUUID || "",
    profilePicture:
      profile && "picture" in profile && profile.picture && profile?.pictureUrl
        ? { uuid: profile.picture, url: profile.pictureUrl }
        : undefined,
    competitorNumber: "",
  };

  return { schema, initialValues };
}
